import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { useFundValidationRulesController } from "./useFundValidationRulesController";
import { NoFundSelected } from "../funds";
import { getAccounts } from "@/api/Accounts";
import { Account } from "@/types/Account";
import { 
  ValidationRulesHeader, 
  ValidationRuleCard, 
  EmptyValidationRulesState 
} from "./components";

export const FundValidationRulesView = () => {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const controller = useFundValidationRulesController();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const accountsData = await getAccounts();
        setAccounts(accountsData);
      } catch (error) {
        // Error handling - accounts will remain empty array
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const toggleRuleExpansion = (ruleId: number) => {
    if (controller.editingStates[ruleId]) {
      return;
    }
    
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const editingRuleIds = Object.keys(controller.editingStates)
      .filter(id => controller.editingStates[parseInt(id)])
      .map(id => parseInt(id));
    
    if (editingRuleIds.length > 0) {
      setExpandedRules(prev => {
        const newSet = new Set(prev);
        editingRuleIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  }, [controller.editingStates]);

  if (!selectedFund) {
    return <NoFundSelected />;
  }

  return (
    <div className="w-full space-y-8">
      <ValidationRulesHeader
        selectedFund={selectedFund}
        isValidating={controller.isValidating}
        isLoading={controller.isLoading}
        validationRulesCount={controller.validationRules.length}
        onValidateRules={controller.validateFundRules}
        onRefreshRules={controller.refreshRules}
      />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <span>Fund Validation Rules</span>
            {controller.validationRules.length > 0 && (
              <Badge variant="secondary">
                {controller.validationRules.length} {controller.validationRules.length === 1 ? 'rule' : 'rules'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {controller.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Loading validation rules...</p>
              </div>
            </div>
          ) : controller.error ? (
            <div className="flex items-center gap-3 p-4 bg-destructive/10 text-destructive rounded-lg">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span className="font-medium">{controller.error}</span>
            </div>
          ) : controller.validationRules.length === 0 ? (
            <EmptyValidationRulesState
              onCreateDraftRule={controller.createDraftRule}
              isSaving={controller.isSaving}
            />
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-6">
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-green-600">
                      {controller.validationRules.filter(rule => !rule.disabled && !controller.isDraftRule(rule.validationRuleId!)).length}
                    </span> active
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-orange-600">
                      {controller.validationRules.filter(rule => rule.disabled && !controller.isDraftRule(rule.validationRuleId!)).length}
                    </span> disabled
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-blue-600">
                      {controller.draftRules.length}
                    </span> drafts
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                {controller.validationRules.map(rule => (
                  <ValidationRuleCard
                    key={rule.validationRuleId}
                    rule={rule}
                    accounts={accounts}
                    accountsLoading={accountsLoading}
                    isEditing={controller.editingStates[rule.validationRuleId!] || false}
                    isDraft={controller.isDraftRule(rule.validationRuleId!)}
                    isSaving={controller.isSaving}
                    supportedRuleTypes={controller.supportedRuleTypes}
                    isExpanded={expandedRules.has(rule.validationRuleId!)}
                    onToggleExpansion={() => toggleRuleExpansion(rule.validationRuleId!)}
                    onStartEditing={controller.startEditing}
                    onSaveRule={controller.saveRule}
                    onCancelEditing={controller.cancelEditing}
                    onUpdateRule={controller.updateRule}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 